import { Autocomplete, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
/**
 * @deprecated this component is deprecated in favor of the Material-UI timepicker - https://mui.com/x/react-date-pickers/time-picker
 *
 * TimePicker component that allows users to select a time from a predefined list of time values.
 * Time values are represented in milliseconds and formatted based on the chosen time format (24h or 12h).
 *
 * @component
 * @param {TimePickerProps} props - The properties to configure the time picker.
 * @param {SxProps} [props.sx] - Style overrides using MUI's `sx` prop.
 * @param {string} props.label - Label displayed for the picker.
 * @param {'24h' | '12h'} [props.timeFormat='24h'] - Format used for displaying time options.
 * @param {1 | 5 | 10 | 15 | 30 | 60} [props.timeMinuteStep=30] - Step interval for time options, in minutes.
 * @param {number} [props.minTime] - Minimum selectable time in milliseconds since start of day.
 * @param {number} [props.maxTime] - Maximum selectable time in milliseconds since start of day.
 * @param {boolean} [props.required] - Marks the field as required.
 * @param {boolean} [props.disabled] - Disables the picker input.
 * @param {boolean} [props.readOnly] - Makes the picker read-only.
 * @param {boolean} [props.fullWidth] - If true, the component takes full width of its container.
 * @param {'small' | 'medium'} [props.size='medium'] - The size of the picker.
 * @param {boolean} [props.error] - If true, displays the picker in an error state.
 * @param {number} [props.tabIndex] - Controls tab indexing for accessibility.
 * @param {TimePickerValue} props.value - The currently selected time, in milliseconds.
 * @param {(newValue: TimePickerValue) => void} props.onChange - Callback when the time value changes.
 * @param {string} [props.'data-test'] - Test identifier for automated tests.
 * @param {boolean} [props.autoFocus] - Autofocus the input when component mounts.
 * @param {string} [props.ariaDescribedBy] - ID of an element that describes the input for screen readers.
 *
 * @returns {JSX.Element} The rendered TimePicker component.
 */
export const TimePicker = (props) => {
    var _a;
    const options = getTimes(props.timeFormat, props.timeMinuteStep, props.value.time, props.minTime, props.maxTime);
    const value = (_a = options.find((option) => option.value === props.value.time)) !== null && _a !== void 0 ? _a : null;
    return (React.createElement(Autocomplete, { sx: Object.assign({ width: '100%', maxWidth: {
                xs: '100%',
                sm: props.fullWidth ? '100%' : 260,
            } }, props.sx), fullWidth: props.fullWidth, size: props.size, value: value, options: options, disabled: props.disabled, readOnly: props.readOnly, renderInput: (params) => {
            var _a;
            return (React.createElement(FormControl, { variant: 'outlined', fullWidth: props.fullWidth, size: params.size, required: props.required, error: props.error, disabled: params.disabled, sx: {
                    width: '100%',
                    '& label.MuiInputLabel-root': { paddingRight: 3 },
                } },
                props.label && (React.createElement(InputLabel, { variant: 'outlined', required: props.required, error: props.error, disabled: props.disabled }, props.label)),
                React.createElement(OutlinedInput, Object.assign({}, params.InputProps, { inputProps: props.tabIndex === -1
                        ? Object.assign(Object.assign({}, params.inputProps), { 'aria-label': props.label, tabIndex: -1 }) : Object.assign(Object.assign({}, params.inputProps), { 'aria-label': props.label }), label: props.label, fullWidth: props.fullWidth, size: params.size, required: props.required, error: props.error, disabled: params.disabled, "data-test": props['data-test'], autoFocus: (_a = props.autoFocus) !== null && _a !== void 0 ? _a : false, "aria-describedby": props.ariaDescribedBy }))));
        }, onChange: (event, option) => {
            const newTime = option === null || option === void 0 ? void 0 : option.value;
            props.onChange({ time: newTime });
        } }));
};
const oneSecondInMs = 1000;
const oneMinuteInMs = 60 * oneSecondInMs;
const oneHourInMs = 60 * oneMinuteInMs;
const oneDayInMs = 24 * oneHourInMs;
function getTimes(timeFormat = '24h', minuteStep = 30, selectedTime, minTime, maxTime) {
    const times = [];
    const stepInMs = minuteStep * oneMinuteInMs;
    for (let value = 0; value < oneDayInMs; value += stepInMs) {
        if (((minTime == null || value >= minTime) && (maxTime == null || value <= maxTime)) || value === selectedTime) {
            times.push({
                label: formatTime(value, timeFormat),
                value,
            });
        }
        if (selectedTime != null && selectedTime > value && selectedTime < value + stepInMs) {
            times.push({
                label: formatTime(value, timeFormat),
                value: selectedTime,
            });
        }
    }
    return times;
}
/**
 * Formats a time value (in milliseconds) into a human-readable string
 * according to the specified time format (12-hour or 24-hour).
 *
 * The function dynamically includes seconds and milliseconds in the output
 * if they are greater than zero.
 *
 * @param {number} value - Time value in milliseconds to format.
 * @param {'24h' | '12h'} [timeFormat='24h'] - Desired time format:
 *   - `'24h'` for 24-hour format (e.g., `14:30`).
 *   - `'12h'` for 12-hour format with AM/PM (e.g., `02:30 PM`).
 * @returns {string} Formatted time string, optionally including seconds or milliseconds.
 */
export function formatTime(value, timeFormat = '24h') {
    const dateZeroTime = new Date(2000, 1, 1).getTime();
    const date = new Date(dateZeroTime + value);
    const ms = date.getTime();
    const second = Math.floor((ms % oneMinuteInMs) / oneSecondInMs);
    const millisecond = Math.floor(ms % oneSecondInMs);
    let formatSuffix = '';
    if (millisecond > 0) {
        formatSuffix = ':ss.SSS';
    }
    else if (second > 0) {
        formatSuffix = ':ss';
    }
    if (timeFormat === '12h') {
        return format(date, 'hh:mm' + formatSuffix + ' a');
    }
    else {
        return format(date, 'HH:mm' + formatSuffix);
    }
}
